import type { AdminPaymentRow, PaymentStatus } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { PAYMENT_STATUS_LABELS } from '@/entities/payment';
import { formatDateTime, formatWon } from '@/shared/lib';
import { Button, cn, StatusBadge, type StatusTone, tableStyles } from '@/shared/ui';

interface PaymentRowProps {
  /** 계약 타입을 그대로 받는다. */
  readonly payment: AdminPaymentRow;
  readonly onOpen: (paymentId: string) => void;
}

/**
 * 결제 상태 배지 톤 — 주문 목록과 같은 기준. neutral 고객 결제 대기 / positive 결제 완료 / warning 일부만 환불된 건 /
 * danger 더 진행되지 않는 건(전액 환불·승인 실패·만료).
 */
const STATUS_TONES: Readonly<Record<PaymentStatus, StatusTone>> = {
  READY: 'neutral',
  DONE: 'positive',
  PARTIAL_CANCELED: 'warning',
  CANCELED: 'danger',
  ABORTED: 'danger',
  EXPIRED: 'danger',
};

const MUTED_CLASS = 'text-muted-foreground';

/** 결제 목록의 한 행 — 상세 버튼을 누르면 결제 상세로 간다. */
export const PaymentRow = ({ payment, onOpen }: PaymentRowProps): ReactElement => {
  const status = PAYMENT_STATUS_LABELS[payment.status];

  return (
    <tr className={tableStyles.row}>
      <td className={cn(tableStyles.td, 'whitespace-nowrap')}>
        {payment.approvedAt ? (
          formatDateTime(payment.approvedAt)
        ) : (
          <span className={MUTED_CLASS}>-</span>
        )}
      </td>
      <td className={cn(tableStyles.td, 'font-medium')}>{payment.orderId}</td>
      <td className={tableStyles.td}>{payment.userEmail}</td>
      <td className={cn(tableStyles.td, 'text-right whitespace-nowrap tabular-nums')}>
        {formatWon(payment.amount)}
      </td>
      <td className={cn(tableStyles.td, 'text-right whitespace-nowrap tabular-nums')}>
        {formatWon(payment.balanceAmount)}
      </td>
      <td className={tableStyles.td}>
        <StatusBadge tone={STATUS_TONES[payment.status]}>{status.label}</StatusBadge>
      </td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap')}>
        {payment.provider === 'BANK_TRANSFER'
          ? '계좌이체'
          : (payment.method ?? <span className={MUTED_CLASS}>-</span>)}
      </td>
      <td className={cn(tableStyles.td, 'text-right')}>
        <Button variant="outline" size="sm" onClick={() => onOpen(payment.id)}>
          상세
        </Button>
      </td>
    </tr>
  );
};
